/* Determines whether the user should be redirected to the onboarding wizard.
   Existing users (who have sources and have never seen the wizard) are auto-marked as completed.
   If the user explicitly resets the wizard (field is `false`), they are NOT auto-marked. */

import { useEffect, useRef } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";

export function useOnboardingGate(enabled = true) {
  const settings = useQuery(api.userSettings.getSettings, enabled ? undefined : "skip");
  const sourceCount = useQuery(api.sources.count, enabled ? undefined : "skip");
  const updateSetting = useMutation(api.userSettings.updateSingleSetting);
  const autoMarkedRef = useRef(false);

  const isLoading = settings === undefined || sourceCount === undefined;
  const wizardCompleted = settings?.onboardingWizardCompleted === true;

  // The field is `undefined` (or settings is null) only for users who have never
  // seen the wizard at all. If it's explicitly `false`, the user intentionally
  // reset it — don't auto-mark.
  const neverSeenWizard = settings !== undefined
    && (settings === null || settings.onboardingWizardCompleted === undefined);

  // Auto-mark existing users who have sources AND have never had the wizard field set
  useEffect(() => {
    if (isLoading || autoMarkedRef.current || wizardCompleted) return;
    if (neverSeenWizard && sourceCount > 0) {
      autoMarkedRef.current = true;
      void updateSetting({ key: "onboardingWizardCompleted", value: true });
      void updateSetting({ key: "inlineTourCompleted", value: true });
    }
  }, [isLoading, sourceCount, wizardCompleted, neverSeenWizard, updateSetting]);

  // Redirect when:
  // - Wizard is explicitly false (user reset it), OR
  // - Brand new user with no sources (neverSeenWizard + sourceCount === 0)
  // Don't redirect when neverSeenWizard + has sources (auto-mark handles that)
  const shouldRedirect = !isLoading && !wizardCompleted
    && (!neverSeenWizard || sourceCount === 0);

  return {
    shouldRedirectToOnboarding: shouldRedirect,
    isLoading,
  };
}
